<template>
  <div class="client-hierarchy-select">
    <div v-if="label" class="text-subtitle-2 mb-2">{{ label }}</div>
    <v-autocomplete
      v-model="selectedValue"
      v-model:search="searchText"
      :items="clientsForSelect"
      item-title="title"
      item-value="value"
      :label="placeholder"
      :loading="loading"
      :disabled="disabled"
      :clearable="clearable"
      :density="density"
      :variant="variant"
      :hide-details="hideDetails"
      @focus="onFocus"
      @update:model-value="onSelectionChange"
    >
      <template v-slot:item="{ item, props }">
        <v-list-item v-bind="props">
          <template v-slot:prepend v-if="item.raw.level > 0">
            <span class="text-grey mr-1">{{ '—'.repeat(item.raw.level) }}</span>
          </template>
        </v-list-item>
      </template>
    </v-autocomplete>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { log } from "@/composables/useLog";
import { getClientTree } from "@/composables/useGetClientsAPI";
import { getCache, setCache } from "@/composables/useLocalCache";
import store from "@/store";

export default defineComponent({
  name: 'ClientHierarchySelect',
  props: {
    modelValue: {
      type: [Number, String],
      default: null
    },
    // Pre-loaded clients list (optional - if not provided, will load from API)
    clientsList: {
      type: Array,
      default: null
    },
    // Client ID to load tree from (optional - uses current user's client if not provided)
    rootClientId: {
      type: [Number, String],
      default: null
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'Select client...'
    },
    includeAllOption: {
      type: Boolean,
      default: false
    },
    allOptionLabel: {
      type: String,
      default: 'All Clients'
    },
    clearable: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    density: {
      type: String,
      default: 'compact'
    },
    variant: {
      type: String,
      default: 'outlined'
    },
    hideDetails: {
      type: Boolean,
      default: true
    },
    autoSelectFirst: {
      type: Boolean,
      default: false
    },
    autoSelectOwn: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'change', 'clientsLoaded'],
  data() {
    return {
      selectedValue: this.modelValue,
      searchText: '',
      loading: false,
      internalClientsList: []
    }
  },
  computed: {
    token() {
      return store.getters.getToken;
    },
    currentUser() {
      return store.getters.getUser;
    },
    effectiveClientsList() {
      // Use provided list or internal loaded list
      return this.clientsList || this.internalClientsList;
    },
    clientsForSelect() {
      const list = [];

      // Add "All" option if enabled
      if (this.includeAllOption) {
        list.push({
          value: null,
          title: this.allOptionLabel,
          level: 0
        });
      }

      // Add clients from hierarchy
      this.effectiveClientsList.forEach(client => {
        list.push({
          value: client.id,
          title: client.displayName || client.name,
          level: client.level || 0,
          name: client.name
        });
      });

      return list;
    }
  },
  watch: {
    modelValue(newVal) {
      this.selectedValue = newVal;
    },
    clientsList: {
      immediate: true,
      handler(newList) {
        if (newList && newList.length > 0) {
          this.handleAutoSelect();
        }
      }
    }
  },
  methods: {
    onFocus() {
      // Clear search text on focus to allow typing
      this.$nextTick(() => {
        this.searchText = '';
      });
    },

    onSelectionChange(value) {
      this.selectedValue = value;
      this.$emit('update:modelValue', value);

      // Find the selected client data
      const selectedClient = this.effectiveClientsList.find(c => c.id === value);
      this.$emit('change', {
        clientId: value,
        client: selectedClient || null
      });
    },

    async loadClients() {
      if (this.clientsList) {
        // Clients provided externally, no need to load
        return;
      }

      const clientId = this.rootClientId || this.currentUser?.client_id;
      if (!clientId) {
        log("ClientHierarchySelect: No client ID available");
        return;
      }

      // Check localStorage cache first
      const cacheKey = `client_tree_${clientId}`;
      const cached = getCache(cacheKey);
      if (cached) {
        this.internalClientsList = cached;
        this.$emit('clientsLoaded', this.internalClientsList);
        this.handleAutoSelect();
        return;
      }

      this.loading = true;
      try {
        const response = await getClientTree(this.token, clientId);
        if (!response.error && response.clients_list) {
          this.internalClientsList = this.buildClientsList(response.clients_list);
          setCache(cacheKey, this.internalClientsList, 10); // 10 min TTL
          this.$emit('clientsLoaded', this.internalClientsList);
          this.handleAutoSelect();
        }
      } catch (error) {
        log("ClientHierarchySelect: Error loading clients:");
        log(error);
      } finally {
        this.loading = false;
      }
    },

    buildClientsList(clientsTree, level = 0) {
      const list = [];
      if (!clientsTree || clientsTree.length === 0) return list;

      clientsTree.forEach(client => {
        const indent = level > 0 ? '—'.repeat(level) + ' ' : '';
        list.push({
          id: client.client_id || client.id,
          name: client.name,
          displayName: indent + client.name,
          level: level
        });

        if (client.children && client.children.length > 0) {
          list.push(...this.buildClientsList(client.children, level + 1));
        }
      });

      return list;
    },

    handleAutoSelect() {
      if (this.selectedValue) return; // Already has a value

      if (this.autoSelectOwn && this.currentUser?.client_id) {
        // Auto-select current user's client
        const ownClient = this.effectiveClientsList.find(c => c.id === this.currentUser.client_id);
        if (ownClient) {
          this.onSelectionChange(ownClient.id);
        }
      } else if (this.autoSelectFirst && this.effectiveClientsList.length > 0) {
        // Auto-select first client in list
        this.onSelectionChange(this.effectiveClientsList[0].id);
      }
    }
  },
  mounted() {
    if (!this.clientsList) {
      this.loadClients();
    } else {
      this.handleAutoSelect();
    }
  }
});
</script>

<style scoped>
.client-hierarchy-select {
  width: 100%;
}
</style>
